<template>
    <div class="container position-relative">
        <!-- Child Info -->
        <div class="title mb-4" v-if="showChildTitle">
            {{ trans('general.page.space_forms.labels.child_info') }}
        </div>
        <div class="row">
            <!-- Image -->
            <div class="col-12 mb-3 d-flex align-items-center gap-2">
                <avatar-image
                    wrap-class="bg-gray-200"
                    ref="avatarImage"
                    :avatarOnly="false"
                    :src="image"
                    v-on:chosen-file="imageChosen($event)"/>

                <basic-button
                    :disabled="!editMode"
                    icon="fi fi-rr-camera"
                    cls="btn-sm btn-outline"
                    :text="form.image ? trans('general.defaults.select_another_image') : trans('general.defaults.select_profile_image')"
                    onclick="document.getElementById('avatarImage').click()"/>

                <basic-button
                    :disabled="!editMode"
                    v-if="form.image || this.form.image_id"
                    icon="fi fi-rr-cross-small"
                    cls="btn-sm btn-outline"
                    :text="trans('general.defaults.remove')"
                    @click="imageDeleted"/>
            </div>

            <!-- Name -->
            <div class="col-12 col-lg-4">
                <basic-input
                    :disabled="!editMode"
                    type="text"
                    :required="true"
                    :label="trans('general.page.children.fields.name')"
                    v-model="form.name"
                    :error="errorFields.name"
                    v-on:reset-error="errorFields.name = null"/>
            </div>

            <!-- Surname -->
            <div class="col-12 col-lg-4">
                <basic-input
                    :disabled="!editMode"
                    type="text"
                    :required="true"
                    :label="trans('general.page.children.fields.surname')"
                    v-model="form.surname"
                    :error="errorFields.surname"
                    v-on:reset-error="errorFields.surname = null"/>
            </div>

            <!-- Birthday -->
            <div class="col-12 col-lg-4">
                <basic-date-picker
                    :disabled="!editMode"
                    mode="date"
                    :required="true"
                    :label="trans('general.page.children.fields.birthday')"
                    v-model="birthday"
                    :error="errorFields.birthday"
                    v-on:reset-error="errorFields.birthday = null"/>
            </div>

            <!-- Class -->
            <div class="col-12 col-lg-4">
                <class-select
                    :disabled="!editMode || groupId !== null"
                    :group-id="groupId"
                    class="mb-3 pb-1"
                    auto-close="true"
                    :multiple="false"
                    :allow-search="true"
                    :required="true"
                    :bulk-selecting="false"
                    :label="trans('general.page.children.fields.group')"
                    :placeholder="trans('general.page.children.fields.select_group')"
                    :value="selectedGroup"
                    :error="errorFields.group_id"
                    v-on:classesChanged="selectedGroup = $event"
                    v-on:reset-error="errorFields.group_id = null"/>
            </div>

            <!-- Gender -->
            <div class="col-12 col-lg-4 d-flex gap-2 align-items-center">
                <label class="large mb-4">
                    {{trans('general.page.children.fields.gender')}}
                </label>
                <basic-input
                    :disabled="!editMode"
                    type="radio"
                    :label="trans('general.gender.female')"
                    name="gender"
                    value="female"
                    v-on:input="form.gender = $event"
                    :checked="form.gender === 'female'"
                    :error="errorFields.gender"
                    v-on:reset-error="errorFields.gender = null"/>

                <basic-input
                    :disabled="!editMode"
                    type="radio"
                    :label="trans('general.gender.male')"
                    name="gender"
                    value="male"
                    v-on:input="form.gender = $event"
                    :checked="form.gender === 'male'"
                    :error="errorFields.gender"
                    v-on:reset-error="errorFields.gender = null"/>
            </div>

            <!-- SocialMedia Share -->
            <div class="col-12 col-lg-4 d-flex align-items-center">
                <basic-input
                    :disabled="!editMode"
                    type="switch"
                    :label="trans('general.page.children.fields.share_on_social_media')"
                    v-model="share_on_social_media"
                    :error="errorFields.share_on_social_media"
                    v-on:reset-error="errorFields.share_on_social_media = null"/>
            </div>

            <!-- Nationality -->
            <div class="col-12 col-lg-4">
                <basic-input
                    :disabled="!editMode"
                    type="text"
                    :required="false"
                    :label="trans('general.page.children.nationality')"
                    v-model="form.nationality"
                    :error="errorFields.nationality"
                    v-on:reset-error="errorFields.nationality = null"/>
            </div>

            <!-- Family Language -->
            <div class="col-12 col-lg-4">
                <basic-input
                    :disabled="!editMode"
                    type="text"
                    :required="false"
                    :label="trans('general.page.children.family_language')"
                    v-model="form.family_language"
                    :error="errorFields.family_language"
                    v-on:reset-error="errorFields.family_language = null"/>
            </div>

            <!-- Address -->
            <div class="col-12 col-lg-4">
                <basic-input
                    :disabled="!editMode"
                    type="text"
                    :required="false"
                    :label="trans('general.page.children.address')"
                    v-model="form.address"
                    :error="errorFields.address"
                    v-on:reset-error="errorFields.address = null"/>
            </div>

            <!-- Notes / Other Info -->
            <div class="col-12 col-lg-4">
                <basic-input
                    :disabled="!editMode"
                    type="text"
                    :required="false"
                    :label="trans('general.page.children.fields.notes')"
                    v-model="form.other_info"
                    :error="errorFields.other_info"
                    v-on:reset-error="errorFields.other_info = null"/>
            </div>

            <!-- Allergies -->
            <div class="col-12 col-lg-4">
                <allergies-select v-model="allergies" v-on:update="updateAllergies" :disabled="!editMode"/>
            </div>

            <!-- Vaccines -->
            <div class="col-12 col-lg-4">
                <vaccines-select v-model="vaccines" v-on:update="updateVaccines" :disabled="!editMode"/>
            </div>
        </div>

        <div v-if="hasOldEmergencyContact">
            <div class="title mt-4 mb-2">
                {{ trans('general.page.children.fields.emergency_contact') }}
            </div>

            <div
                class="alert alert-warning mt-2 mb-3"
                v-if="form.emergency_name !== '' || form.emergency_number !== '' && hasAnyRole(['super_admin', 'administrator'])"
                v-html="trans('general.child.emergency_deprecated')"></div>

            <div class="row">
                <div class="col-12 col-lg-4">
                    <basic-input
                        :disabled="!editMode || (form.emergency_name === '' && form.emergency_number === '')"
                        type="text"
                        :required="false"
                        :label="trans('general.page.children.fields.emergency_name')"
                        v-model="form.emergency_name"
                        :error="errorFields.emergency_name"
                        v-on:reset-error="errorFields.emergency_name = null"/>
                </div>
                <div class="col-12 col-lg-4">
                    <basic-input
                        :disabled="!editMode || (form.emergency_name === '' && form.emergency_number === '')"
                        type="text"
                        :required="false"
                        :label="trans('general.page.children.fields.emergency_phone')"
                        v-model="form.emergency_number"
                        :error="errorFields.emergency_number"
                        v-on:reset-error="errorFields.emergency_number = null"/>
                </div>
                <basic-button
                    :disabled="!editMode"
                    v-if="form.emergency_name !== '' || form.emergency_number !== ''"
                    icon="fi fi-rr-cross-small"
                    cls="btn-sm btn-outline"
                    @click="form.emergency_name = ''; form.emergency_number = ''"/>
            </div>
        </div>

        <span v-if="assignRelatives">
            <!-- Relatives -->
            <div class="title mt-4 mb-2">
                {{ trans('general.children.assigned_relatives') }}
            </div>

            <assign-relatives v-model="relatives" v-on:add-new="addingRelative" :profile="profile" :disabled="!editMode"/>
        </span>

        <!-- Submit -->
        <div class="d-flex justify-content-end align-items-center col-12 gap-2 mt-4" v-if="editMode">
            <span v-if="!assignToParentButton">

            <!-- Archive Button -->
            <div v-if="childId && hasAnyRole(['super_admin', 'administrator'])"
                 class="btn-group no-arrow dropup dropup-center">
                <basic-button
                    cls="btn-delete btn-secondary  dropdown-toggle deactivate-btn"
                    icon="fi-rr-eye-crossed"
                    data-bs-toggle="dropdown"
                    :text="trans('general.defaults.deactivate')"
                    aria-expanded="false"/>

                <ul class="dropdown-menu">
                    <li><h6 class="dropdown-header">{{trans("general.component.archive_popup.title")}}</h6></li>

                    <li @click="archive" class="btn btn-link dropdown-item text-danger" role="button">
                        {{trans("general.component.archive_popup.yes")}}
                    </li>
                    <li class="btn btn-link dropdown-item text-success" role="button">
                        {{trans("general.component.archive_popup.no")}}
                    </li>
                </ul>
            </div>

                <!-- create & Add another Button -->
            <basic-button
                v-if="!childId"
                :disabled="submitting || disabled"
                cls="btn-secondary"
                :text="trans('general.defaults.create_and_add_another')"
                :loading="saveAndAddAnother"
                @click="submit(true)"/>

                <!-- create Button -->
            <basic-button
                :disabled="submitting || disabled"
                cls="btn-primary"
                :text="trans('general.defaults.save')"
                :loading="save"
                @click="submit()"/>
            </span>

            <!-- Assign to Parent Button -->
            <basic-button
                v-if="assignToParentButton"
                :disabled="submitting || disabled"
                cls="btn-success"
                :text="trans('general.children.save_and_assign')"
                :loading="save"
                @click="submit()"/>
        </div>

        <div class="loader" v-if="childId && !childDataLoaded">
            <div class="spinner-border text-primary position-relative" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    </div>
</template>

<script>
import AvatarImage from "../../../theme/atoms/AvatarImage.vue";
import BasicInput from "../../../theme/atoms/BasicInput.vue";
import BasicButton from "../../../theme/atoms/BasicButton.vue";
import BasicDatePicker from "../../../theme/atoms/BasicDatePicker.vue";
import ClassSelect from "../../../theme/atoms/ClassSelect.vue";
import AllergiesSelect from "../../../theme/molecules/AllergiesSelect.vue";
import VaccinesSelect from "../../../theme/molecules/VaccinesSelect.vue";
import axios from "axios";
import {handleErrors} from "../../../services/ErrorHandler";
import AssignRelatives from "../molecules/AssignRelatives.vue";

export default {
    name: "AddEditChildManually",
    props: {
        childId: {
            type: Number,
            default: null
        },
        hasAnyRole: {
            type: Function,
            required: false,
        },
        allowEdit: {
            type: Boolean,
            default: true,
        },
        assignRelatives: {
            type: Boolean,
            default: true,
        },
        submitData: {
            type: Boolean,
            default: true,
        },
        showChildTitle: {
            type: Boolean,
            default: true,
        },
        assignToParentButton: {
            type: Boolean,
            default: false,
        },
        groupId: {
            type: Number,
            required: false,
            default: null
        },
        profile: {
            type: Boolean,
            default: false,
        }
    },
    components: {
        AssignRelatives,
        AvatarImage,
        ClassSelect,
        BasicDatePicker,
        BasicInput,
        BasicButton,
        AllergiesSelect,
        VaccinesSelect
    },
    data() {
        return {
            childDataLoaded: false,
            disabled: false,
            submitting: false,
            save: false,
            saveAndAddAnother: false,
            selectedGroup: [],
            birthday: null,
            share_on_social_media: false,
            image: null,
            form: {
                image: null,
                name: null,
                surname: null,
                birthday: null,
                group_id: null,
                gender: null,
                share_on_social_media: 0,
                nationality: null,
                family_language: null,
                address: null,
                other_info: null,
                allergies: [],
                vaccines: [],
                relatives: [],
            },
            assignedRelatives: [],
            errors: [],
            errorFields: {
                name: null,
                surname: null,
                birthday: null,
                group_id: null,
            },
            childRelatives: [],
            allergies: [],
            vaccines: [],
            relatives: [],
            editMode: this.allowEdit || this.hasAnyRole(['super_admin', 'administrator']),
            hasOldEmergencyContact: false,
        }
    },
    watch: {
        birthday: {
            handler(val) {
                if (val) {
                    const [day, month, year] = val.split('-').map(Number);
                    const date = new Date(year, month - 1, day);

                    this.form.birthday = [
                        date.getFullYear(),
                        String(date.getMonth() + 1).padStart(2, '0'),
                        String(date.getDate()).padStart(2, '0')
                    ].join('-');
                } else {
                    this.form.birthday = null;
                }
            }
        },
        share_on_social_media: {
            handler: function (val) {
                this.form.share_on_social_media = val ? 1 : 0;
            }
        },
        relatives: {
            handler(val) {
                this.form.relatives = val.map(item => {
                    return {
                        ...item,
                        relation: item.relation[0].id
                    }
                });
            },
            deep: true
        },
        image: {
            handler(val) {
                this.form.image = val;
            }
        },
        selectedGroup: {
            handler(val) {
                this.form.group_id = val.length ? val[0].id : null;
            }
        }
    },
    methods: {
        addingRelative(val) {
            this.disabled = !!val;
        },
        imageChosen(file) {
            this.form.image = file;
        },
        imageDeleted() {
            this.form.image = null;
            this.$refs.avatarImage.reset();

            if (this.childId && this.form.image_id) {
                this.$refs.avatarImage.deleteImage(this.form.image_id);
            }
        },
        updateAllergies(items) {
            this.form.allergies = items.map(item => item.id);
        },
        updateVaccines(items) {
            this.form.vaccines = items.map(item => ({text: item.label}));
        },
        submit(addAnother = false) {
            this.submitting = true;

            if(!this.submitData) {
                this.validateData();

                if (this.errors.length) {
                    this.submitting = false;
                    return;
                }

                this.$emit('new-child', this.form);
                this.submitting = false;
                return;
            }

            if (addAnother) {
                this.saveAndAddAnother = true;
            } else {
                this.save = true;
            }

            let formData = this.getFormData();

            axios.post('/children/save-and-assign-relatives', formData)
                .then(response => {
                    this.$emit('child-saved', this.childId ? true : this.saveAndAddAnother);

                    if(! this.childId) {
                        this.reset();
                    }
                })
                .catch((error) => {
                    handleErrors(error, this, this.errorFields);
                })
                .finally(() => {
                    this.submitting = false;
                    this.save = false;
                    this.saveAndAddAnother = false;
                    this.disabled = false;
                });
        },
        getFormData() {
            let formData = new FormData();

            for (let key in this.form) {
                if (this.form.hasOwnProperty(key)) {
                    const value = this.form[key];

                    if (value !== null && value !== undefined) {
                        if (Array.isArray(value)) {
                            formData.append(key, JSON.stringify(value));
                        } else {
                            formData.append(key, value);
                        }
                    }
                }
            }

            if(this.childId) {
                formData.append('child_id', this.childId);
            }

            if (this.image && this.image instanceof File) {
                formData.append('image', this.image);
            }

            return formData;
        },
        reset() {
            if(! this.groupId) {
                this.selectedGroup = [];
            }
            this.birthday =null;
            this.share_on_social_media =false;
            this.form = {
                image: null,
                name: null,
                surname: null,
                birthday: null,
                group_id: ! this.groupId ? null : this.groupId,
                gender: null,
                share_on_social_media: 0,
                nationality: null,
                family_language: null,
                address: null,
                other_info: null,
                allergies: [],
                vaccines: [],
                relatives: [],
            };
            this.assignedRelatives =[];
            this.errors =[];
            this.errorFields = {
                name: null,
                surname: null,
                birthday: null,
                group_id: null,
                nationality: null,
                family_language: null,
                address: null,
                other_info: null,
            };
            this.childRelatives =[];
            this.allergies =[];
            this.vaccines =[];
            this.relatives =[];
        },
        loadChildData() {
            axios.get(`/children/read/${this.childId}`, {params: {include: 'image;group;allergies;parents' }})
                .then(response => {
                    const child = response.data.data;
                    this.form = {
                        ...child,
                        image: null,
                        image_id: child.image ? child.image.id : null,
                    };
                    this.image = child.image ? child.image.sizes.sm : null;
                    this.selectedGroup = [
                        {
                            id: child.group.id,
                            label: child.group.name
                        }
                    ];
                    this.share_on_social_media = child.share_on_social_media === 1;
                    this.birthday = child.birthday ? child.birthday.split(' ')[0] : null;
                    this.allergies = child.allergies.map(allergy => {
                        return {
                            id: allergy.id,
                            label: allergy.name
                        }
                    });
                    this.vaccines = JSON.parse(child.vaccines ?? "[]").map(vaccine => {
                        return {
                            id: vaccine.text,
                            label: vaccine.text
                        }
                    });
                    this.relatives = child.parents.map(parent => {
                        return {
                            id: parent.id,
                            label: parent.name + ' ' + parent.surname,
                            is_emergency_contact: parent.pivot.is_emergency_contact === 1,
                            relation: [
                                {
                                    id: parent.pivot.relation,
                                    label: trans(`general.page.children.fields.relation.${parent.pivot.relation}`),
                                }
                            ],
                            type: 'existing',
                        }
                    });

                    this.hasOldEmergencyContact = (child.emergency_name && child.emergency_name !== '') || (child.emergency_number && child.emergency_number !== '');
                    this.childDataLoaded = true;
                })
                .catch((error) => {
                    handleErrors(error, this, this.errorFields);
                });
        },
        archive() {
            this.submitting = false;
            axios.delete(`/children/${this.childId}`).then(() => {
                this.$notify({type: 'success', text: trans('general.child.archived')});
                this.$emit('child-archived');
                this.reset();
            }).catch(error => {
                handleErrors(error, this, this.errorFields);
            });
        },
        validateData() {
            Object.keys(this.errorFields).forEach(key => {
                if (!this.form[key]) {
                    this.errorFields[key] = trans('validation.required', { attribute: trans('general.page.children.fields.' + key) });
                    this.errors.push(key);
                }
            });

            if (this.form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
                this.errorFields.email = trans('validation.email', { attribute: trans('general.page.profile.fields.email') });
                this.errors.push('email');
            }
        },
    },
    mounted() {
        this.reset();

        if (this.childId) {
            this.loadChildData();
        }
    }
}

</script>

<style scoped>
.group {
    margin-bottom: 20px;
}
.loader,
.loader::before
{
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
}

.loader::before {
    content: ' ';
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 100;
}

.loader .spinner-border {
    z-index: 101;
}
</style>
